"use client";

import { calculateUsageChange, type UsageChange } from "@/lib/usage-comparison";
import { UsageUnavailable } from "@/components/usage-unavailable";
import { UsageStatCard } from "@/components/usage-stat-card";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import useSWR from "swr";
import {
  getDailyStatisticsLast30Days,
  getDailyStatisticsPrevious30Days,
  type MonthlyUsageStats,
} from "@/services/statistics.service";

interface StatItem extends UsageChange {
  title: string;
  value: string;
  description: string;
}

const formatNumber = (num: number): string => num.toLocaleString();

const sumDailyStats = (days: MonthlyUsageStats[]) =>
  days.reduce(
    (acc, day) => ({
      merchantApi: acc.merchantApi + (day.merchantApi || 0),
      youzuLens: acc.youzuLens + (day.youzuLens || 0),
      propertyVisualize: acc.propertyVisualize + (day.propertyVisualize || 0),
      roomVisualizer: acc.roomVisualizer + (day.roomVisualizer || 0),
      magicBackground: acc.magicBackground + (day.magicBackground || 0),
      twoDTo3D: acc.twoDTo3D + (day.twoDTo3D || 0),
    }),
    { merchantApi: 0, youzuLens: 0, propertyVisualize: 0, roomVisualizer: 0, magicBackground: 0, twoDTo3D: 0 },
  );

export function OverviewStats() {
  const { data: dailyResponse, error, isLoading } = useSWR(
    "daily-stats-last-30-days",
    getDailyStatisticsLast30Days,
    { refreshInterval: 60000 },
  );

  const { data: previousDailyResponse, error: previousError, isLoading: previousLoading } = useSWR(
    "daily-stats-previous-30-days",
    getDailyStatisticsPrevious30Days,
    { refreshInterval: 60000 },
  );

  if (error || dailyResponse?.success === false) return <UsageUnavailable />;

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Card
            key={i}
            className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-100"
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div className="h-4 w-24 bg-purple-200 animate-pulse rounded" />
            </CardHeader>
            <CardContent>
              <div className="h-8 w-16 bg-purple-200 animate-pulse rounded mb-2" />
              <div className="h-3 w-32 bg-purple-200 animate-pulse rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!dailyResponse?.success) return <UsageUnavailable />;

  const totals = sumDailyStats(dailyResponse.data);
  const prev = !previousError && previousDailyResponse?.success ? sumDailyStats(previousDailyResponse.data) : undefined;

  const stats: StatItem[] = [
    {
      title: "Merchant API",
      value: formatNumber(totals.merchantApi),
      description: "Successful calls · Last 30 days",
      ...calculateUsageChange(totals.merchantApi, prev?.merchantApi),
    },
    {
      title: "Youzu Lens",
      value: formatNumber(totals.youzuLens),
      description: "Visual Searches · Last 30 days",
      ...calculateUsageChange(totals.youzuLens, prev?.youzuLens),
    },
    {
      title: "Property Visualize",
      value: formatNumber(totals.propertyVisualize),
      description: "Property Views · Last 30 days",
      ...calculateUsageChange(totals.propertyVisualize, prev?.propertyVisualize),
    },
    {
      title: "Room Visualizer",
      value: formatNumber(totals.roomVisualizer),
      description: "Room Designs · Last 30 days",
      ...calculateUsageChange(totals.roomVisualizer, prev?.roomVisualizer),
    },
    {
      title: "Magic Background",
      value: formatNumber(totals.magicBackground),
      description: "Background Creations · Last 30 days",
      ...calculateUsageChange(totals.magicBackground, prev?.magicBackground),
    },
    {
      title: "2D to 3D",
      value: formatNumber(totals.twoDTo3D),
      description: "Models Created · Last 30 days",
      ...calculateUsageChange(totals.twoDTo3D, prev?.twoDTo3D),
    },
  ];

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
      {stats.map((stat) => (
        <UsageStatCard key={stat.title} title={stat.title} value={stat.value}
          description={stat.description} comparison={stat}
          period="previous 30 days"
          comparisonLoading={!previousError && previousDailyResponse?.success !== false && previousLoading} />
      ))}
    </div>
  );
}
